<template>
  <div>
    <Base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info">
      <b-row class="align-items-center">
        <b-col>
          <h6 class="h2 text-white d-inline-block mb-0">Export Builder</h6>
          <p class="text-white-50 mb-0 mt-1">Pilih kolom dan filter untuk membuat export kustom</p>
        </b-col>
      </b-row>
    </Base-header>

    <b-container fluid class="mt--6">
      <b-row>
        <!-- Left: Column Picker -->
        <b-col xl="5">
          <b-card>
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="h3 mb-0"><i class="ni ni-bullet-list-67 text-primary mr-2"></i>Pilih Kolom</h5>
                <span class="badge badge-primary">{{ selectedColumns.length }} dipilih</span>
              </div>
            </template>

            <!-- Preset Loader -->
            <b-form-group label="Preset" label-size="sm" class="mb-3">
              <div class="d-flex">
                <b-form-select v-model="selectedPreset" size="sm" class="mr-2">
                  <option :value="null">-- Pilih Preset --</option>
                  <option v-for="p in presets" :key="p.id" :value="p.id">
                    {{ p.name }} {{ p.is_system ? '(sistem)' : '' }}
                  </option>
                </b-form-select>
                <b-button variant="outline-primary" size="sm" @click="loadPreset" :disabled="!selectedPreset">
                  <i class="fa fa-download"></i>
                </b-button>
              </div>
            </b-form-group>

            <!-- Search -->
            <b-form-input v-model="columnSearch" placeholder="Cari kolom..." size="sm" class="mb-3"></b-form-input>

            <!-- Column Groups -->
            <div v-for="group in filteredGroups" :key="group.name" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="small text-uppercase text-muted">{{ group.name }}</strong>
                <b-button variant="link" size="sm" class="p-0 text-primary" @click="toggleGroup(group.name)">
                  {{ isGroupAllSelected(group.name) ? 'Hapus semua' : 'Pilih semua' }}
                </b-button>
              </div>
              <div class="column-list">
                <div
                  v-for="col in group.columns"
                  :key="col.key"
                  class="column-item d-flex align-items-center px-2 py-1 rounded mb-1"
                  :class="{ 'bg-primary-light': isSelected(col.key) }"
                  @click="toggleColumn(col.key)"
                  style="cursor: pointer"
                >
                  <b-form-checkbox
                    :checked="isSelected(col.key)"
                    @change="toggleColumn(col.key)"
                    size="sm"
                    class="mr-0"
                    @click.native.stop
                  ></b-form-checkbox>
                  <span class="small ml-1">{{ col.label }}</span>
                  <span v-if="col.source === 'dynamic'" class="badge badge-light ml-auto" style="font-size: 0.65rem">survei</span>
                </div>
              </div>
            </div>

            <div v-if="filteredGroups.length === 0" class="text-center text-muted py-3">
              <small>Tidak ada kolom yang cocok</small>
            </div>

            <!-- Quick Actions -->
            <div class="mt-3 d-flex justify-content-between">
              <b-button variant="outline-secondary" size="sm" @click="selectedColumns = []">Reset</b-button>
              <b-button variant="outline-success" size="sm" @click="showSavePreset = true" :disabled="selectedColumns.length === 0">
                <i class="fa fa-save mr-1"></i>Simpan Preset
              </b-button>
            </div>
          </b-card>
        </b-col>

        <!-- Right: Filters + Preview + Download -->
        <b-col xl="7">
          <!-- Filters -->
          <b-card class="mb-4">
            <template #header>
              <h5 class="h3 mb-0"><i class="ni ni-ui-04 text-info mr-2"></i>Filter</h5>
            </template>
            <b-row>
              <b-col md="3">
                <b-form-group label="Tahun Lulus" label-size="sm">
                  <b-form-select v-model="filters.tahun" :options="tahunOptions" size="sm"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Fakultas" label-size="sm">
                  <b-form-select v-model="filters.id_fakultas" :options="fakultasOptions" size="sm" @change="onFakultasChange"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Program Studi" label-size="sm">
                  <b-form-select v-model="filters.id_prodi" :options="prodiOptions" size="sm"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Status Lulusan" label-size="sm">
                  <b-form-select v-model="filters.status_lulusan" :options="statusOptions" size="sm"></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <!-- Selected Columns Order -->
          <b-card class="mb-4" v-if="selectedColumns.length > 0">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="h3 mb-0"><i class="ni ni-align-left-2 text-warning mr-2"></i>Urutan Kolom</h5>
                <small class="text-muted">Klik × untuk hapus</small>
              </div>
            </template>
            <div class="d-flex flex-wrap">
              <span
                v-for="(col, idx) in selectedColumns"
                :key="col"
                class="badge badge-primary mr-1 mb-1 px-2 py-1"
                style="font-size: 0.8rem; cursor: pointer"
              >
                {{ idx + 1 }}. {{ getLabel(col) }}
                <span class="ml-1" @click.stop="removeColumn(col)" style="opacity: 0.7">&times;</span>
              </span>
            </div>
          </b-card>

          <!-- Actions -->
          <b-card class="mb-4">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <b-button variant="info" size="sm" @click="doPreview" :disabled="selectedColumns.length === 0 || previewLoading">
                  <i class="fa fa-eye mr-1"></i>Preview (10 baris)
                  <b-spinner v-if="previewLoading" small class="ml-1"></b-spinner>
                </b-button>
              </div>
              <div>
                <b-button variant="success" @click="doDownload" :disabled="selectedColumns.length === 0 || downloadLoading">
                  <i class="fa fa-file-excel-o mr-1"></i>Download Excel
                  <b-spinner v-if="downloadLoading" small class="ml-1"></b-spinner>
                </b-button>
              </div>
            </div>
          </b-card>

          <!-- Preview Table -->
          <b-card v-if="previewData.length > 0" class="mb-5" no-body>
            <b-card-header>
              <h5 class="h3 mb-0">Preview Data</h5>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover mb-0" style="font-size: 0.8rem">
                  <thead class="thead-light">
                    <tr>
                      <th>#</th>
                      <th v-for="col in previewColumns" :key="col">{{ getLabel(col) }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in previewData" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td v-for="col in previewColumns" :key="col">{{ getCellValue(row, col) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Save Preset Modal -->
      <b-modal v-model="showSavePreset" title="Simpan Preset" size="sm" @ok="savePreset">
        <b-form-group label="Nama Preset" label-size="sm">
          <b-form-input v-model="newPresetName" size="sm" placeholder="Contoh: Laporan Tahunan"></b-form-input>
        </b-form-group>
        <b-form-group label="Deskripsi (opsional)" label-size="sm">
          <b-form-input v-model="newPresetDesc" size="sm"></b-form-input>
        </b-form-group>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import BaseHeader from "@/components/admin/BaseHeader";
import ExportBuilderService from "@/services/export-builder.service";
import DataService from "@/services/data.service";

export default {
  components: { BaseHeader },
  data: function () {
    return {
      allColumns: [],
      selectedColumns: [],
      columnSearch: "",

      filters: {
        tahun: null,
        id_fakultas: null,
        id_prodi: null,
        status_lulusan: null
      },

      tahunOptions: [{ value: null, text: "Semua Tahun" }],
      fakultasOptions: [{ value: null, text: "Semua Fakultas" }],
      allProdiList: [],
      prodiOptions: [{ value: null, text: "Semua Prodi" }],
      statusOptions: [
        { value: null, text: "Semua Status" },
        { value: 0, text: "Belum Bekerja" },
        { value: 1, text: "Bekerja" },
        { value: 2, text: "Berwirausaha" },
        { value: 3, text: "Melanjutkan Studi" }
      ],

      presets: [],
      selectedPreset: null,
      showSavePreset: false,
      newPresetName: "",
      newPresetDesc: "",

      previewLoading: false,
      previewData: [],
      previewColumns: [],

      downloadLoading: false
    };
  },
  computed: {
    groupedColumns: function () {
      var groups = {};
      var self = this;
      self.allColumns.forEach(function (col) {
        if (!groups[col.group]) {
          groups[col.group] = [];
        }
        groups[col.group].push(col);
      });
      var result = [];
      var order = ["Alumni", "Akademik", "Tracer", "IKU"];
      order.forEach(function (g) {
        if (groups[g]) {
          result.push({ name: g, columns: groups[g] });
          delete groups[g];
        }
      });
      Object.keys(groups).sort().forEach(function (g) {
        result.push({ name: g, columns: groups[g] });
      });
      return result;
    },
    filteredGroups: function () {
      var search = this.columnSearch.toLowerCase().trim();
      if (!search) return this.groupedColumns;
      return this.groupedColumns.map(function (g) {
        return {
          name: g.name,
          columns: g.columns.filter(function (c) {
            return c.label.toLowerCase().indexOf(search) !== -1 ||
              c.key.toLowerCase().indexOf(search) !== -1;
          })
        };
      }).filter(function (g) { return g.columns.length > 0; });
    }
  },
  created: function () {
    this.loadColumns();
    this.loadPresets();
    this.loadFilters();
  },
  methods: {
    loadColumns: function () {
      var self = this;
      ExportBuilderService.getColumns().then(function (res) {
        self.allColumns = res.data || [];
      }).catch(function () {});
    },
    loadPresets: function () {
      var self = this;
      ExportBuilderService.getPresets().then(function (res) {
        self.presets = res.data || [];
      }).catch(function () {});
    },
    loadFilters: function () {
      var self = this;
      var currentYear = new Date().getFullYear();
      for (var y = currentYear; y >= 2019; y--) {
        self.tahunOptions.push({ value: y, text: "Lulusan " + y });
      }

      DataService.getFaculties().then(function (res) {
        var list = [{ value: null, text: "Semua Fakultas" }];
        var faculties = (res.data && res.data.faculties) || [];
        faculties.forEach(function (f) {
          list.push({ value: f.id_fakultas, text: f.nm_fakultas });
        });
        self.fakultasOptions = list;
      }).catch(function () {});

      DataService.getStudyPrograms().then(function (res) {
        self.allProdiList = (res.data && res.data.studyPrograms) || [];
        self.buildProdiOptions();
      }).catch(function () {});
    },
    onFakultasChange: function () {
      this.filters.id_prodi = null;
      this.buildProdiOptions();
    },
    buildProdiOptions: function () {
      var self = this;
      var list = [{ value: null, text: "Semua Prodi" }];
      self.allProdiList.forEach(function (p) {
        if (!self.filters.id_fakultas || p.id_fakultas === self.filters.id_fakultas) {
          list.push({
            value: p.id_program_studi,
            text: p.nm_program_studi + " (" + (p.jenjang || "") + ")"
          });
        }
      });
      self.prodiOptions = list;
    },
    isSelected: function (key) {
      return this.selectedColumns.indexOf(key) !== -1;
    },
    toggleColumn: function (key) {
      var idx = this.selectedColumns.indexOf(key);
      if (idx === -1) {
        this.selectedColumns.push(key);
      } else {
        this.selectedColumns.splice(idx, 1);
      }
    },
    removeColumn: function (key) {
      var idx = this.selectedColumns.indexOf(key);
      if (idx !== -1) this.selectedColumns.splice(idx, 1);
    },
    isGroupAllSelected: function (groupName) {
      var self = this;
      var group = this.groupedColumns.find(function (g) { return g.name === groupName; });
      if (!group) return false;
      return group.columns.every(function (c) { return self.isSelected(c.key); });
    },
    toggleGroup: function (groupName) {
      var self = this;
      var group = this.groupedColumns.find(function (g) { return g.name === groupName; });
      if (!group) return;
      if (self.isGroupAllSelected(groupName)) {
        group.columns.forEach(function (c) {
          var idx = self.selectedColumns.indexOf(c.key);
          if (idx !== -1) self.selectedColumns.splice(idx, 1);
        });
      } else {
        group.columns.forEach(function (c) {
          if (self.selectedColumns.indexOf(c.key) === -1) {
            self.selectedColumns.push(c.key);
          }
        });
      }
    },
    getLabel: function (key) {
      var col = this.allColumns.find(function (c) { return c.key === key; });
      return col ? col.label : key;
    },
    loadPreset: function () {
      var self = this;
      var preset = this.presets.find(function (p) { return p.id === self.selectedPreset; });
      if (!preset) return;
      self.selectedColumns = (preset.columns || []).slice();
      if (preset.filters) {
        if (preset.filters.tahun) self.filters.tahun = preset.filters.tahun;
        if (preset.filters.status_lulusan !== undefined) self.filters.status_lulusan = preset.filters.status_lulusan;
        if (preset.filters.id_fakultas) self.filters.id_fakultas = preset.filters.id_fakultas;
        if (preset.filters.id_prodi) self.filters.id_prodi = preset.filters.id_prodi;
      }
    },
    savePreset: function () {
      var self = this;
      if (!self.newPresetName) return;
      ExportBuilderService.savePreset(
        self.newPresetName,
        self.newPresetDesc,
        self.selectedColumns,
        self.filters
      ).then(function () {
        self.showSavePreset = false;
        self.newPresetName = "";
        self.newPresetDesc = "";
        self.loadPresets();
      }).catch(function (err) {
        var msg = (err.response && err.response.data && err.response.data.message) || "Gagal menyimpan preset";
        alert(msg);
      });
    },
    doPreview: function () {
      var self = this;
      self.previewLoading = true;
      self.previewData = [];
      ExportBuilderService.preview(self.selectedColumns, self.filters).then(function (res) {
        self.previewColumns = res.data.columns || [];
        self.previewData = res.data.data || [];
        self.previewLoading = false;
      }).catch(function () {
        self.previewLoading = false;
        alert("Gagal mengambil preview");
      });
    },
    getCellValue: function (row, col) {
      var colName = col.indexOf("dyn:") === 0 ? col.substring(4) : col;
      var val = row[colName];
      if (val === null || val === undefined) return "";
      if (col === "status_lulusan") {
        var map = { 0: "Belum Bekerja", 1: "Bekerja", 2: "Berwirausaha", 3: "Melanjutkan Studi" };
        return map[val] || val;
      }
      return val;
    },
    doDownload: function () {
      var self = this;
      self.downloadLoading = true;
      ExportBuilderService.build(self.selectedColumns, self.filters).then(function (res) {
        self.downloadLoading = false;
        var blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        var tahun = self.filters.tahun || "all";
        a.href = url;
        a.download = "export_tracer_" + tahun + ".xlsx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }).catch(function () {
        self.downloadLoading = false;
        alert("Gagal mengunduh file");
      });
    }
  }
};
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(94, 114, 228, 0.1);
}
.column-list {
  max-height: 220px;
  overflow-y: auto;
}
.column-item:hover {
  background-color: rgba(94, 114, 228, 0.05);
}
</style>
